import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { InvestorActorGuard } from './guards/investor-actor.guard';
import { DeveloperActorGuard } from './guards/developer-actor.guard';
import { subpartnerActorGuard } from './guards/subpartner-actor.guard';
import { SalesActorGuard } from './guards/sales-actor.guard';
import { AdminActorGuard } from './guards/admin-actor.guard';
import { AlreadyAuthenticatedGuard } from './guards/already-authenticated.guard';
import { AuthLayoutComponent } from './layout/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { AdminLayoutComponent } from './layout/admin-layout/admin-layout.component';
import { LandingPageComponent } from './pages/landing/landing-page.component';
import { LoginPageComponent } from './pages/auth/login/login-page.component';
import { SignupChoiceComponent } from './pages/auth/signup/signup-choice.component';
import { SignupWelcomeComponent } from './pages/auth/signup/signup-welcome.component';
import { CustomerSignupComponent } from './pages/auth/signup/customer-signup.component';
import { DeveloperSignupComponent } from './pages/auth/signup/developer-signup.component';
import { PropertiesPageComponent } from './pages/properties/properties-page.component';
import { ProjectsPageComponent } from './pages/projects/projects-page.component';
import { FaqPageComponent } from './pages/faq/faq-page.component';
import { DeveloperDashboardComponent } from './pages/developer/dashboard/developer-dashboard.component';
import { DeveloperPropertiesComponent } from './pages/developer/properties/developer-properties.component';
import { PropertyCreateComponent } from './pages/developer/properties/create/property-create.component';
import { DeveloperProjectsComponent } from './pages/developer/projects/developer-projects.component';
import { DeveloperProjectDetailComponent } from './pages/developer/projects/developer-project-detail.component';
import { DeveloperPhasesComponent } from './pages/developer/phases/developer-phases.component';
import { DeveloperPropertyDetailComponent } from './pages/developer/properties/view/developer-property-detail.component';
import { DeveloperPropertyEditComponent } from './pages/developer/properties/edit/developer-property-edit.component';
import { OrgMediaLibraryComponent } from './pages/shared/org-media-library/org-media-library.component';
import { CustomerLayoutComponent } from './layout/customer-layout/customer-layout.component';

export const appRoutes: Routes = [
  // External viewer (no layout — mobile header injected inside page)
  {
    path: 'viewer',
    loadComponent: () =>
      import('./pages/viewer/external-viewer-page.component').then(
        (m) => m.ExternalViewerPageComponent,
      ),
  },

  // File share page (no layout — standalone page for shared files)
  {
    path: 'share/file/:fileId',
    loadComponent: () =>
      import('./pages/share/file-share.component').then(
        (m) => m.FileShareComponent,
      ),
  },

  // Notification deep-link redirect. The notification catalog emits neutral
  // `/n/<kind>[/<id>]` links; this component resolves them to a role-appropriate
  // route (investor → customer-dashboard, org → developer). Mounted at the
  // root so it works regardless of the user's current layout.
  {
    path: 'n/:kind',
    loadComponent: () =>
      import('./pages/notifications/notification-redirect.component').then(
        (m) => m.NotificationRedirectComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'n/:kind/:id',
    loadComponent: () =>
      import('./pages/notifications/notification-redirect.component').then(
        (m) => m.NotificationRedirectComponent,
      ),
    canActivate: [AuthGuard],
  },

  // Public Routes (Auth Layout)
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      { path: '', component: LandingPageComponent },
      {
        path: 'login',
        component: LoginPageComponent,
        canActivate: [AlreadyAuthenticatedGuard],
      },
      {
        path: 'verify-email',
        loadComponent: () =>
          import('./pages/auth/verify-email/verify-email-page.component').then(
            (m) => m.VerifyEmailPageComponent,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/auth/forgot-password/forgot-password-page.component').then(
            (m) => m.ForgotPasswordPageComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/auth/reset-password/reset-password-page.component').then(
            (m) => m.ResetPasswordPageComponent,
          ),
      },
      {
        path: 'terms',
        loadComponent: () =>
          import('./pages/legal/policy-page.component').then(
            (m) => m.PolicyPageComponent,
          ),
        data: { policy: 'terms' },
      },
      {
        path: 'privacy',
        loadComponent: () =>
          import('./pages/legal/policy-page.component').then(
            (m) => m.PolicyPageComponent,
          ),
        data: { policy: 'privacy' },
      },
      {
        path: 'delete-account',
        loadComponent: () =>
          import('./pages/legal/policy-page.component').then(
            (m) => m.PolicyPageComponent,
          ),
        data: { policy: 'delete-account' },
        canActivate: [AuthGuard],
      },
      {
        path: 'signup',
        component: SignupWelcomeComponent,
        canActivate: [AlreadyAuthenticatedGuard],
      },
      {
        path: 'signup/customer',
        component: CustomerSignupComponent,
        canActivate: [AlreadyAuthenticatedGuard],
      },
      {
        path: 'signup/developer',
        component: DeveloperSignupComponent,
        canActivate: [AlreadyAuthenticatedGuard],
      },
      {
        path: 'form',
        loadComponent: () =>
          import('./pages/form/system-lead-form.component').then(
            (m) => m.SystemLeadFormComponent,
          ),
      },
      { path: 'properties', component: PropertiesPageComponent },
      {
        path: 'properties/:id',
        loadComponent: () =>
          import('./features/public-property-detail/property-detail.page').then(
            (m) => m.PropertyDetailPageComponent,
          ),
      },
      { path: 'projects', component: ProjectsPageComponent },
      {
        path: 'projects/:id',
        loadComponent: () =>
          import('./pages/projects/detail/project-detail.component').then(
            (m) => m.ProjectDetailComponent,
          ),
      },
      { path: 'faq', component: FaqPageComponent },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile-page.component').then(
            (m) => m.ProfilePageComponent,
          ),
        canActivate: [AuthGuard],
      },
    ],
  },

  // Developer Routes (Main Layout)
  {
    path: 'developer',
    component: MainLayoutComponent,
    canActivate: [AuthGuard, DeveloperActorGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DeveloperDashboardComponent },
      { path: 'projects', component: DeveloperProjectsComponent },
      { path: 'projects/new', component: DeveloperProjectsComponent },
      { path: 'projects/:id', component: DeveloperProjectDetailComponent },
      { path: 'phases', component: DeveloperPhasesComponent },
      { path: 'properties/create', component: PropertyCreateComponent },
      {
        path: 'properties/view/:id',
        component: DeveloperPropertyDetailComponent,
      },
      {
        path: 'properties/edit/:id',
        component: DeveloperPropertyEditComponent,
      },
      { path: 'properties', component: DeveloperPropertiesComponent },
      {
        path: 'leads/:leadId',
        loadComponent: () =>
          import('./pages/developer/leads/view/developer-lead-profile.component').then(
            (m) => m.DeveloperLeadProfileComponent,
          ),
      },
      {
        path: 'leads',
        loadComponent: () =>
          import('./pages/developer/leads/developer-leads.component').then(
            (m) => m.DeveloperLeadsComponent,
          ),
      },
      {
        path: 'reservations/settings',
        loadComponent: () =>
          import(
            './pages/developer/reservations/settings/developer-reservation-settings.component'
          ).then((m) => m.DeveloperReservationSettingsComponent),
      },
      {
        path: 'reservations',
        loadComponent: () =>
          import('./features/developer-reservations/developer-reservations.page').then(
            (m) => m.DeveloperReservationsPageComponent,
          ),
      },
      // 2026-09-13: Signed Investors / Signed Units pages hidden (user
      // request). Signed reservations are visible through the leads list
      // (?signed=true is still available on the API for exports/scripts).
      { path: 'signed-investors', redirectTo: 'leads', pathMatch: 'full' },
      { path: 'signed-units', redirectTo: 'leads', pathMatch: 'full' },
      { path: 'offers', redirectTo: 'offers/unit', pathMatch: 'full' },
      {
        path: 'offers/:tab',
        loadComponent: () =>
          import('./features/developer-offers/developer-offers.page').then(
            (m) => m.DeveloperOffersPageComponent,
          ),
      },
      {
        path: 'media/:tab',
        component: OrgMediaLibraryComponent,
        data: { workspace: 'developer' },
      },
      {
        path: 'partners',
        loadComponent: () =>
          import('./features/partners/developer-partners.page').then(
            (m) => m.DeveloperPartnersPageComponent,
          ),
      },
      { path: 'partners', redirectTo: 'partners', pathMatch: 'full' },
      {
        path: 'sales-team',
        loadComponent: () =>
          import('./pages/developer/sales-team/developer-sales-team.component').then(
            (m) => m.DeveloperSalesTeamComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/developer/settings/developer-settings.component').then(
            (m) => m.DeveloperSettingsComponent,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notification-center.component').then(
            (m) => m.NotificationCenterComponent,
          ),
      },
    ],
  },

  // Customer Dashboard Routes
  {
    path: 'customer-dashboard',
    component: CustomerLayoutComponent,
    canActivate: [AuthGuard, InvestorActorGuard],
    children: [
      { path: '', redirectTo: 'reservations', pathMatch: 'full' },
      {
        path: 'reservations',
        loadComponent: () =>
          import('./pages/customer/reservations/customer-reservations.component').then(
            (m) => m.CustomerReservationsComponent,
          ),
      },
      {
        path: 'offers',
        loadComponent: () =>
          import('./pages/customer/offers/customer-offers.component').then(
            (m) => m.CustomerOffersComponent,
          ),
      },
      {
        path: 'offers/:id',
        loadComponent: () =>
          import('./pages/customer/offers/customer-offer-detail.component').then(
            (m) => m.CustomerOfferDetailComponent,
          ),
      },
    ],
  },

  // partner Dashboard Routes
  {
    path: 'partner',
    component: MainLayoutComponent,
    canActivate: [AuthGuard, subpartnerActorGuard],
    children: [
      // 2026-09-13: the Assignments page is hidden (user request) — partner
      // actors land on the leads list, which includes their assigned leads.
      { path: '', redirectTo: '/developer/leads', pathMatch: 'full' },
      {
        path: 'dashboard',
        redirectTo: '/developer/leads',
        pathMatch: 'full',
      },
      {
        path: 'portfolio',
        redirectTo: '/partner/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'projects',
        redirectTo: '/developer/projects',
        pathMatch: 'full',
      },
      {
        path: 'projects/:id',
        redirectTo: '/developer/projects/:id',
        pathMatch: 'full',
      },
      { path: 'phases', redirectTo: '/developer/phases', pathMatch: 'full' },
      {
        path: 'properties',
        redirectTo: '/developer/properties',
        pathMatch: 'full',
      },
      {
        path: 'properties/view/:id',
        redirectTo: '/developer/properties/view/:id',
        pathMatch: 'full',
      },
      {
        path: 'properties/edit/:id',
        redirectTo: '/developer/properties/edit/:id',
        pathMatch: 'full',
      },
      {
        path: 'leads/:leadId',
        redirectTo: '/developer/leads/:leadId',
        pathMatch: 'full',
      },
      { path: 'leads', redirectTo: '/developer/leads', pathMatch: 'full' },
      { path: 'offers', redirectTo: '/developer/offers', pathMatch: 'full' },
      {
        path: 'offers/:tab',
        redirectTo: '/developer/offers/:tab',
        pathMatch: 'full',
      },
      {
        path: 'media/:tab',
        redirectTo: '/developer/media/:tab',
        pathMatch: 'full',
      },
      {
        path: 'settings',
        redirectTo: '/developer/settings',
        pathMatch: 'full',
      },
    ],
  },

    // Sales Dashboard Routes
    {
    path: 'sales',
    component: MainLayoutComponent,
    canActivate: [AuthGuard, SalesActorGuard],
    children: [
      // 2026-09-13: the Assignments page is hidden (user request) — sales
      // actors land on the leads list, which includes their assigned leads.
      { path: '', redirectTo: '/developer/leads', pathMatch: 'full' },
      {
        path: 'dashboard',
        redirectTo: '/developer/leads',
        pathMatch: 'full',
      },
      {
        path: 'portfolio',
        redirectTo: '/sales/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'projects',
        redirectTo: '/developer/projects',
        pathMatch: 'full',
      },
      {
        path: 'projects/:id',
        redirectTo: '/developer/projects/:id',
        pathMatch: 'full',
      },
      { path: 'phases', redirectTo: '/developer/phases', pathMatch: 'full' },
      {
        path: 'properties',
        redirectTo: '/developer/properties',
        pathMatch: 'full',
      },
      {
        path: 'properties/view/:id',
        redirectTo: '/developer/properties/view/:id',
        pathMatch: 'full',
      },
      {
        path: 'properties/edit/:id',
        redirectTo: '/developer/properties/edit/:id',
        pathMatch: 'full',
      },
      {
        path: 'leads/:leadId',
        redirectTo: '/developer/leads/:leadId',
        pathMatch: 'full',
      },
      { path: 'leads', redirectTo: '/developer/leads', pathMatch: 'full' },
      { path: 'offers', redirectTo: '/developer/offers', pathMatch: 'full' },
      {
        path: 'offers/:tab',
        redirectTo: '/developer/offers/:tab',
        pathMatch: 'full',
      },
      {
        path: 'media/:tab',
        redirectTo: '/developer/media/:tab',
        pathMatch: 'full',
      },
      {
        path: 'settings',
        redirectTo: '/developer/settings',
        pathMatch: 'full',
      },
    ],
  },

  // Admin Routes (Main Layout)
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard, AdminActorGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/admin/dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent,
          ),
      },
      {
        path: 'property-approvals',
        loadComponent: () =>
          import('./pages/admin/property-approvals/admin-property-approvals.component').then(
            (m) => m.AdminPropertyApprovalsComponent,
          ),
      },
      {
        path: 'project-approvals',
        loadComponent: () =>
          import('./pages/admin/project-approvals/admin-project-approvals.component').then(
            (m) => m.AdminProjectApprovalsComponent,
          ),
      },
      {
        path: 'projects',
        loadComponent: () =>
          import('./pages/admin/projects/admin-projects.component').then(
            (m) => m.AdminProjectsComponent,
          ),
      },
      {
        path: 'projects/verify',
        loadComponent: () =>
          import('./pages/admin/projects/verify/admin-project-verification.component').then(
            (m) => m.AdminProjectVerificationComponent,
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/admin/users/admin-users.component').then(
            (m) => m.AdminUsersComponent,
          ),
      },
      {
        path: 'signup-links',
        loadComponent: () =>
          import('./pages/admin/signup-links/admin-signup-links.component').then(
            (m) => m.AdminSignupLinksComponent,
          ),
      },
      {
        path: 'companies',
        loadComponent: () =>
          import('./pages/admin/organizations/admin-organizations.component').then(
            (m) => m.AdminOrganizationsComponent,
          ),
      },
      { path: 'organizations', redirectTo: 'companies', pathMatch: 'full' },
      {
        path: 'leads',
        loadComponent: () =>
          import('./pages/admin/leads/admin-leads.component').then(
            (m) => m.AdminLeadsComponent,
          ),
      },
      {
        path: 'audit-logs',
        loadComponent: () =>
          import('./pages/admin/audit-logs/admin-audit-logs.component').then(
            (m) => m.AdminAuditLogsComponent,
          ),
      },
      {
        path: 'system-leads',
        loadComponent: () =>
          import('./pages/admin/system-leads/admin-system-leads.component').then(
            (m) => m.AdminSystemLeadsComponent,
          ),
      },
      {
        path: 'faqs',
        loadComponent: () =>
          import('./pages/admin/faqs/admin-faqs.component').then(
            (m) => m.AdminFaqsComponent,
          ),
      },
      {
        path: 'identity-reviews',
        loadComponent: () =>
          import('./pages/admin/identity-reviews/admin-identity-reviews.component').then(
            (m) => m.AdminIdentityReviewsComponent,
          ),
      },
      {
        path: 'deleted/properties',
        loadComponent: () =>
          import('./pages/admin/deleted/properties/admin-deleted-properties.component').then(
            (m) => m.AdminDeletedPropertiesComponent,
          ),
      },
      {
        path: 'deleted/projects',
        loadComponent: () =>
          import('./pages/admin/deleted/projects/admin-deleted-projects.component').then(
            (m) => m.AdminDeletedProjectsComponent,
          ),
      },
      {
        path: 'projects/view/:id',
        loadComponent: () =>
          import('./pages/admin/projects/view/admin-project-detail.component').then(
            (m) => m.AdminProjectDetailComponent,
          ),
      },
      { path: 'projects/edit/:id', component: DeveloperProjectDetailComponent },
      {
        path: 'properties',
        loadComponent: () =>
          import('./pages/admin/properties/admin-properties.component').then(
            (m) => m.AdminPropertiesComponent,
          ),
      },
      {
        path: 'properties/view/:id',
        loadComponent: () =>
          import('./pages/admin/properties/view/admin-property-detail.component').then(
            (m) => m.AdminPropertyDetailComponent,
          ),
      },
      {
        path: 'properties/edit/:id',
        component: DeveloperPropertyEditComponent,
      },
      {
        path: 'media/:tab',
        component: OrgMediaLibraryComponent,
        data: { workspace: 'admin' },
      },
      {
        path: 'prod-to-dev-sync',
        loadComponent: () =>
          import('./pages/admin/prod-to-dev-sync/admin-prod-to-dev-sync.component').then(
            (m) => m.AdminProdToDevSyncComponent,
          ),
      },
      {
        path: 'system-features',
        loadComponent: () =>
          import('./pages/admin/system-features/admin-system-features.component').then(
            (m) => m.AdminSystemFeaturesComponent,
          ),
      },
      {
        path: 'reservation-template',
        loadComponent: () =>
          import('./pages/admin/reservation-template/admin-reservation-template.component').then(
            (m) => m.AdminReservationTemplateComponent,
          ),
      },
      {
        path: 'features/new',
        loadComponent: () =>
          import('./pages/admin/features/admin-feature-create.component').then(
            (m) => m.AdminFeatureCreateComponent,
          ),
      },
      {
        path: 'features/:id/edit',
        loadComponent: () =>
          import('./pages/admin/features/admin-feature-edit.component').then(
            (m) => m.AdminFeatureEditComponent,
          ),
      },
      {
        path: 'features',
        loadComponent: () =>
          import('./pages/admin/features/admin-features.component').then(
            (m) => m.AdminFeaturesComponent,
          ),
      },
      {
        path: 'legal-policies',
        loadComponent: () =>
          import('./pages/admin/legal-policies/admin-legal-policies.component').then(
            (m) => m.AdminLegalPoliciesComponent,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/admin/notifications/admin-notifications.component').then(
            (m) => m.AdminNotificationsComponent,
          ),
      },
    ],
  },

  // V2 design-preview tree (isolated, unlinked, unguarded). See app/v2/.
  {
    path: 'v2',
    loadChildren: () => import('./v2/v2.routes').then((m) => m.V2_ROUTES),
  },

  // Fallback
  { path: '**', redirectTo: '' },
];
